import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTeamContext } from '../../contexts/TeamContext';
import { FaArrowLeft, FaSync } from 'react-icons/fa';
import { ChipsUsedControls } from './ChipsUsedControls';
import { ChipsUsedList } from './ChipsUsedList';
import { ChipName, ChipEvent, ChipsUsedRow } from './../../types';
import { formatDate } from '../utils/functions';
import {useCurrentGameweek} from '../../hooks/useCurrentGameweek';
import IconButton from '../UI/IconButton';
import ToggleButton from '../UI/ToggleButton';
import Badge from '../UI/Badge';
import { getChipMeta } from '../UI/chipMeta';

// ---- Small UI bits ----
export const Pill: React.FC<React.PropsWithChildren<{ active?: boolean; onClick?: () => void; title?: string }>> = ({ active, onClick, title, children }) => (
  <ToggleButton active={!!active} onClick={onClick} title={title}>
    {children}
  </ToggleButton>
);

export const ChipBadge: React.FC<{ name: string; event: number; time?: string }> = ({ name, event, time }) => {
  const { Icon, label, tone } = getChipMeta(name);
  return (
    <Badge
      tone={tone}
      className="min-w-[120px] justify-between rounded-xl"
      title={`${label} – GW${event}${time ? ` • ${formatDate(time)}` : ''}`}
    >
      <span className="flex items-center gap-2 truncate">
        <Icon className="shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span className="font-semibold">GW {event}</span>
    </Badge>
  );
};

export const EmptyBadge: React.FC<{ expected: string }> = ({ expected }) => {
  const { Icon, label } = getChipMeta(expected);
  return (
    <Badge
      tone="neutral"
      className="min-w-[120px] justify-between rounded-xl opacity-70"
      title={`${label} – unused`}
    >
      <span className="flex items-center gap-2 truncate">
        <Icon className="shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span className="font-semibold">—</span>
    </Badge>
  );
};


const ChipsUsed: React.FC = () => {
  const navigate = useNavigate();
  const { teams } = useTeamContext();
  const { currentGameweek } = useCurrentGameweek();

  const [loading, setLoading] = useState<boolean>(true);
  const [chipsUsed, setChipsUsed] = useState<ChipsUsedRow[]>([]);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ChipName | 'all'>('all');
  const [sort, setSort] = useState<'name' | 'count' | 'recent'>('recent');

  useEffect(() => {
    if (!teams.length) return;

    const API_BASE_URL = process.env.REACT_APP_API_URL as string;

    const fetchChips = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          teams.map(async (t: any) => {
            const response = await axios.get(`${API_BASE_URL}/api/${t.teamId}/history`);
            return {
              teamId: t.teamId as string,
              managerName: t.playerName as string,
              chips: (response.data.chips ?? []) as ChipEvent[],
            } as ChipsUsedRow;
          })
        );
        setChipsUsed(results);
      } catch (err) {
        console.error(err);
        setChipsUsed([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChips();
  }, [teams]);

  const EXPECTED_CHIPS: ChipName[] = ['wildcard', 'freehit', 'bboost', '3xc'];

  const filtered = useMemo(() => {
    let rows = chipsUsed;

    if (query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter(r => r.managerName.toLowerCase().includes(q) || r.teamId.toString().includes(q));
    }

    if (filter !== 'all') {
      rows = rows.filter(r => r.chips.some(c => c.name?.toLowerCase() === filter));
    }

    // sort
    rows = [...rows].sort((a, b) => {
      if (sort === 'name') return a.managerName.localeCompare(b.managerName);
      if (sort === 'count') return (b.chips.length - a.chips.length) || a.managerName.localeCompare(b.managerName);
      // recent
      const maxA = Math.max(0, ...a.chips.map(c => c.event || 0));
      const maxB = Math.max(0, ...b.chips.map(c => c.event || 0));
      if (maxB !== maxA) return maxB - maxA;
      return a.managerName.localeCompare(b.managerName);
    });

    return rows;
  }, [chipsUsed, query, filter, sort]);

  return (
    <div className="mx-auto max-w-6xl px-3 sm:px-4 md:px-6 py-4 text-content">
      <div className="flex items-center gap-3 mb-4">
        <IconButton label="Go back" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </IconButton>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">League Chip Tracker</h1>
          <p className="text-content-secondary text-sm">See who used what, and when.</p>
        </div>
      </div>

      <ChipsUsedControls
        query={query}
        setQuery={setQuery}
        filter={filter}
        setFilter={setFilter}
        sort={sort}
        setSort={setSort}
      />
      <div className="rounded-2xl bg-surface-sunken ring-1 ring-line p-2 sm:p-3">
        {loading ? (
          <div className="p-6 text-center text-content-secondary">
            <div className="inline-flex items-center gap-2">
              <FaSync className="animate-spin" />
              <span>Loading chip data…</span>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-content-secondary">No managers match your filters.</div>
        ) : (
          <ChipsUsedList rows={filtered} expectedChips={EXPECTED_CHIPS} currentGameweek={currentGameweek} />
        )}
      </div>
    </div>
  );
};

export default ChipsUsed;
